import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect } from 'react'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import Icon from '../../components/Icon'
import FilterBar from '../../components/FilterBar'
import { Input, Select, SearchableSelect, Button, Pagination } from '../../components/ui'
import ConfirmModal from '../../components/ConfirmModal'
import { getStageStatusClass } from '../../utils/stageStatus'
import { buildStageTypeFilterOptions } from './stageTypeFilterOptions'
import './Company.css'

function Stages() {
  const { filters, setFilters, pagination, setPagination, search, setSearch } = useListState({
    filters: {
      client_id: '',
      stage_type: '',
      object_id: '',
      status: '',
      planned_date_from: '',
      planned_date_to: '',
      distribution_type: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 1
    },
    search: '',
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canCreate = user?.permissions?.['stages.create']
  const [stages, setStages] = useState([])
  const [contracts, setContracts] = useState([])
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [selectedContractId, setSelectedContractId] = useState('')
  const [contractSearch, setContractSearch] = useState('')
  const [clients, setClients] = useState([])
  const [objects, setObjects] = useState([])
  const [stageTypes, setStageTypes] = useState([])
  const [distributionTypes, setDistributionTypes] = useState([])
  const [loading, setLoading] = useState(true)

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  const effectiveSearch = (search || '').trim().length >= 3 ? (search || '').trim() : ''
  const stageTypeOptions = React.useMemo(() => buildStageTypeFilterOptions(stageTypes), [stageTypes])

  useEffect(() => {
    fetchContracts()
    fetchClients()
    fetchObjects()
    fetchStageTypes()
    fetchDistributionTypes()
  }, [])

  const fetchContracts = async () => {
    try {
      const response = await api.get('/company/contracts?limit=1000')
      if (response.data?.contracts) {
        setContracts(response.data.contracts)
      }
    } catch (error) {
      console.error('Error fetching contracts:', error)
    }
  }

  useEffect(() => {
    fetchStages()
  }, [
    filters.client_id,
    filters.stage_type,
    filters.object_id,
    filters.status,
    filters.planned_date_from,
    filters.planned_date_to,
    filters.distribution_type,
    pagination.page,
    effectiveSearch
  ])

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchClients = async () => {
    try {
      const res = await api.get('/company/clients?limit=1000')
      if (res.data?.clients) setClients(res.data.clients)
    } catch (e) {
      console.error('Error fetching clients:', e)
    }
  }

  const fetchObjects = async () => {
    try {
      const res = await api.get('/company/objects?limit=1000')
      if (res.data?.objects) setObjects(res.data.objects)
    } catch (e) {
      console.error('Error fetching objects:', e)
    }
  }

  const fetchStageTypes = async () => {
    try {
      const res = await api.get('/company/stage-types')
      if (res.data?.stage_types) setStageTypes(res.data.stage_types)
    } catch (e) {
      console.error('Error fetching stage types:', e)
    }
  }

  const fetchDistributionTypes = async () => {
    try {
      const res = await api.get('/company/stages/distribution-types')
      if (res.data?.distribution_types) setDistributionTypes(res.data.distribution_types)
    } catch (e) {
      console.error('Error fetching distribution types:', e)
    }
  }

  const fetchStages = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.stage_type) params.append('stage_type', filters.stage_type)
      if (filters.object_id) params.append('object_id', filters.object_id)
      if (filters.status) params.append('status', filters.status)
      if (filters.planned_date_from) params.append('planned_date_from', filters.planned_date_from)
      if (filters.planned_date_to) params.append('planned_date_to', filters.planned_date_to)
      if (filters.distribution_type) params.append('distribution_type', filters.distribution_type)
      if (effectiveSearch) params.append('search', effectiveSearch)
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())

      const res = await api.get(`/company/stages?${params}`)
      if (res.data?.stages) {
        setStages(res.data.stages)
        setPagination(prev => ({
          ...prev,
          total: res.data.total ?? 0,
          pages: res.data.pages ?? 1
        }))
      } else {
        setStages([])
      }
    } catch (e) {
      console.error('Error fetching stages:', e)
      setStages([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handleSearchChange = (value) => {
    const hadEffective = (search || '').trim().length >= 3
    const hasEffective = (value || '').trim().length >= 3
    setSearch(value)
    if (hadEffective !== hasEffective) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.client_id) count++
    if (filters.stage_type) count++
    if (filters.object_id) count++
    if (filters.status) count++
    if (filters.planned_date_from) count++
    if (filters.planned_date_to) count++
    if (filters.distribution_type) count++
    if (effectiveSearch) count++
    return count
  }

  const clearAllFilters = () => {
    setFilters({
      client_id: '',
      stage_type: '',
      object_id: '',
      status: '',
      planned_date_from: '',
      planned_date_to: '',
      distribution_type: ''
    })
    setSearch('')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ru-RU')
  }

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '-'
    const num = typeof amount === 'string' ? parseFloat(amount.replace(',', '.')) : amount
    if (isNaN(num) || !isFinite(num)) return '-'
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(num)
  }

  const isStageCostZero = (stage) => {
    const raw = stage?.cost
    if (raw === null || raw === undefined || raw === '') return true
    const num = typeof raw === 'string' ? parseFloat(raw.replace(',', '.')) : Number(raw)
    return !isFinite(num) || num === 0
  }

  const stageZeroCostTitle = 'Стоимость стадии не установлена (0 ₽). Задайте фактическую стоимость, чтобы включить стадию в распределение.'

  const budgetRulesUrl = (distributionType, distributionTypeId) => {
    if (!distributionType) return '/admin/budget/budget-rules'
    return `/admin/budget/budget-rules/${encodeURIComponent(distributionTypeId || distributionType)}/edit`
  }

  const formatStageLabel = (stage) => {
    const contractNumber = (stage?.contract_number || '').trim()
    const stageType = (stage?.distribution_type || stage?.type || '').trim()
    if (contractNumber && stageType) return `${contractNumber} - ${stageType}`
    return stageType || contractNumber || (stage?.id || '-')
  }

  const handleCreateStage = () => {
    if (contracts.length === 0) return
    if (contracts.length === 1) {
      navigate(`/company/contracts/${contracts[0].id}/stages/new`)
      return
    }
    setContractSearch('')
    setSelectedContractId(contracts[0]?.id || '')
    setCreateModalOpen(true)
  }

  const handleCreateStageConfirm = () => {
    if (!selectedContractId) return
    setCreateModalOpen(false)
    setContractSearch('')
    navigate(`/company/contracts/${selectedContractId}/stages/new`)
  }

  const filteredContractsForCreate = contracts.filter((c) =>
    String(c.number || '')
      .toLowerCase()
      .includes(contractSearch.trim().toLowerCase())
  )
  const isSelectedContractVisible = filteredContractsForCreate.some((c) => c.id === selectedContractId)

  usePageHeader({
    renderActions: () => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
        {canCreate && (
          <Button
            variant="primary"
            type="button"
            onClick={handleCreateStage}
            disabled={contracts.length === 0}
            title={contracts.length === 0 ? 'Нет договоров для создания бюджета стадии' : 'Добавить бюджет стадии'}
          >
            Добавить бюджет стадии
          </Button>
        )}
      </div>
    )
  }, [canCreate, contracts.length, navigate, selectedContractId, contractSearch])

  if (loading) {
    return (
      <div className="company-page">
        <div className="loading-skeleton">
          <div className="skeleton-header" />
          <div className="skeleton-filters">
            <div className="skeleton-filter" />
            <div className="skeleton-filter" />
            <div className="skeleton-filter" />
          </div>
          <div className="skeleton-table">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="company-page">
      {createModalOpen && (
        <ConfirmModal
          title="Создание бюджета стадии"
          message={(
            <div className="create-act-modal-content">
              <p>Выберите договор для создания бюджета стадии:</p>
              <div className="filter-group">
                <Input
                  type="text"
                  placeholder="Поиск по номеру договора"
                  value={contractSearch}
                  onChange={(e) => {
                    const next = e.target.value
                    setContractSearch(next)
                    const nextFiltered = contracts.filter((c) =>
                      String(c.number || '')
                        .toLowerCase()
                        .includes(next.trim().toLowerCase())
                    )
                    if (nextFiltered.length === 0) {
                      setSelectedContractId('')
                    } else if (!nextFiltered.some((c) => c.id === selectedContractId)) {
                      setSelectedContractId(nextFiltered[0].id)
                    }
                  }}
                  aria-label="Поиск договора по номеру"
                />
              </div>
              <div className="filter-group">
                <Select
                  value={selectedContractId}
                  onChange={(e) => setSelectedContractId(e.target.value)}
                  aria-label="Договор"
                >
                  <option value="">— Выберите договор —</option>
                  {filteredContractsForCreate.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.number || '-'}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}
          confirmLabel="Добавить бюджет стадии"
          cancelLabel="Отмена"
          confirmVariant="primary"
          onConfirm={handleCreateStageConfirm}
          onCancel={() => setCreateModalOpen(false)}
          confirmDisabled={!selectedContractId || !isSelectedContractVisible}
        />
      )}
      <div className="filters-section">
        <FilterBar
          expandable
          expanded={filtersExpanded}
          onToggle={() => setFiltersExpanded(!filtersExpanded)}
          filterCount={getActiveFiltersCount()}
          onClear={clearAllFilters}
          clearLabel={isMobile ? 'Сбросить все' : 'Очистить фильтры'}
          isMobile={isMobile}
        >
          <div className="filter-group filter-group-search">
            <label>Поиск</label>
            <Input
              type="text"
              placeholder="ID стадии, название, объект"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Поиск по стадии"
            />
          </div>
          <div className="filter-group">
            <label>Клиент</label>
            <Select
              value={filters.client_id}
              onChange={(e) => handleFilterChange('client_id', e.target.value)}
              aria-label="Клиент"
            >
              <option value="">Все клиенты</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label htmlFor="stages-stage-type-filter">Тип стадии</label>
            <SearchableSelect
              id="stages-stage-type-filter"
              className="stage-type-filter-select"
              value={filters.stage_type}
              placeholder="Все типы"
              options={[{ value: '', label: 'Все типы' }, ...stageTypeOptions]}
              emptyText="Типы стадий не найдены"
              onChange={(value) => handleFilterChange('stage_type', value)}
            />
          </div>
          <div className="filter-group">
            <label>Статус</label>
            <Select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              aria-label="Статус стадии"
            >
              <option value="">Все статусы</option>
              <option value="не в работе">не в работе</option>
              <option value="в работе">в работе</option>
              <option value="работы завершены">работы завершены</option>
              <option value="закрыта">закрыта</option>
            </Select>
          </div>
          <div className="filter-group">
            <label>Объект</label>
            <Select
              value={filters.object_id}
              onChange={(e) => handleFilterChange('object_id', e.target.value)}
              aria-label="Объект"
            >
              <option value="">Все объекты</option>
              {objects.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Тип распределения</label>
            <Select
              value={filters.distribution_type}
              onChange={(e) => handleFilterChange('distribution_type', e.target.value)}
              aria-label="Тип распределения"
            >
              <option value="">Все типы</option>
              {distributionTypes.map((dt) => (
                <option key={dt} value={dt}>{dt}</option>
              ))}
            </Select>
          </div>
          <div className="filters-dates-row">
            <div className="filter-group">
              <label>Срок с</label>
              <Input
                type="date"
                value={filters.planned_date_from || ''}
                onChange={(e) => handleFilterChange('planned_date_from', e.target.value)}
                className="date-picker-input"
                aria-label="Плановая дата с"
              />
            </div>
            <div className="filter-group">
              <label>Срок по</label>
              <Input
                type="date"
                value={filters.planned_date_to || ''}
                onChange={(e) => handleFilterChange('planned_date_to', e.target.value)}
                className="date-picker-input"
                aria-label="Плановая дата по"
              />
            </div>
          </div>
        </FilterBar>
      </div>

      <div className="table-section">
        {stages.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon">
              <Icon name="file" size={48} />
            </div>
            <div className="no-data-text">Стадии не найдены</div>
            {getActiveFiltersCount() > 0 && (
              <Button variant="ghost" className="no-data-button" onClick={clearAllFilters}>
                Сбросить фильтры
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="table-summary-compact">
              Найдено {pagination.total} стадий
            </div>
            {isMobile ? (
              <>
              <div className="stages-cards acts-cards">
                {stages.map((stage) => {
                  const zeroCost = isStageCostZero(stage)
                  return (
                  <div
                    key={stage.id}
                    className={`act-card ui-card ui-card-hover stages-card${zeroCost ? ' act-card-mismatch' : ''}`}
                    title={zeroCost ? stageZeroCostTitle : undefined}
                    onClick={() => navigate(`/stage/${encodeURIComponent(stage.public_id || stage.id)}`)}
                  >
                    <div className="act-card-header">
                      <div className="act-card-title">
                        <Link className="act-number" to={`/stage/${encodeURIComponent(stage.public_id || stage.id)}`}>{formatStageLabel(stage)}</Link>
                        {(stage.status != null && stage.status !== '') && (
                          <span className={`object-status-badge ${getStageStatusClass(stage.status)}`}>
                            {stage.status}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="act-card-body">
                      <div className="act-card-row">
                        <span className="act-card-label">Объект:</span>
                        <Link className="act-card-value clickable-link" to={stage.object_id ? `/object/${stage.object_id}` : undefined}
                        >
                          {stage.object_name || '-'}
                        </Link>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Тип распределения:</span>
                        <span className="act-card-value">
                          {stage.distribution_type ? (
                            <Link
                              to={budgetRulesUrl(stage.distribution_type, stage.distribution_type_id)}
                              className="clickable-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {stage.distribution_type}
                            </Link>
                          ) : '-'}
                        </span>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Плановая дата:</span>
                        <span className="act-card-value">{formatDate(stage.planned_date)}</span>
                      </div>
                      <div className="act-card-row act-card-row-highlight">
                        <span className="act-card-label">Стоимость:</span>
                        <span className="act-card-value act-card-amount">{formatAmount(stage.cost)}</span>
                      </div>
                      <div className="act-card-row">
                        <span className="act-card-label">Статус:</span>
                        <span className="act-card-value">
                          {(stage.status != null && stage.status !== '') ? (
                            <span className={`object-status-badge ${getStageStatusClass(stage.status)}`}>
                              {stage.status}
                            </span>
                          ) : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                )
                })}
              </div>
                {pagination.total > 0 && (
                  <div className="company-pagination-bar-wrap">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="company-table-frame">
              <div className="table-responsive">
                <table className="ui-table data-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Объект</th>
                      <th>Тип распределения</th>
                      <th className="text-right">Плановая дата</th>
                      <th className="text-right">Стоимость</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stages.map((stage) => {
                      const zeroCost = isStageCostZero(stage)
                      return (
                      <tr
                        key={stage.id}
                        className={`clickable-row${zeroCost ? ' act-row-mismatch' : ''}`}
                        title={zeroCost ? stageZeroCostTitle : undefined}
                        onClick={() => navigate(`/stage/${encodeURIComponent(stage.public_id || stage.id)}`)}
                      >
                        <td>
                          <Link to={`/stage/${encodeURIComponent(stage.public_id || stage.id)}`} className="list-primary-name">{formatStageLabel(stage)}</Link>
                        </td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            if (stage.object_id) navigate(`/object/${stage.object_id}`)
                          }}
                        >
                          {stage.object_id ? (
                            <Link className="ui-table-entity-link" to={stage.object_id ? `/object/${stage.object_id}` : undefined}>{stage.object_name || '-'}</Link>
                          ) : (
                            stage.object_name || '-'
                          )}
                        </td>
                        <td>
                          {stage.distribution_type ? (
                            <Link
                              to={budgetRulesUrl(stage.distribution_type, stage.distribution_type_id)}
                              className="ui-table-entity-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {stage.distribution_type}
                            </Link>
                          ) : '-'}
                        </td>
                        <td className="text-right mono">{formatDate(stage.planned_date)}</td>
                        <td className="text-right mono">{formatAmount(stage.cost)}</td>
                        <td>
                          {(stage.status != null && stage.status !== '') ? (
                            <span className={`object-status-badge ${getStageStatusClass(stage.status)}`}>
                              {stage.status}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Stages
